import React, { Fragment, useRef } from 'react';

import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import Button from 'epn-ui/umd/dist/components/Button';
import Loader from 'epn-ui/umd/dist/components/Loader';
import TooltipPro from 'epn-ui/umd/dist/components/TooltipPro';

import { IGetCommonStatDataFormatted } from '@api/types';
import BaseSemanticLink from '@base/BaseSemanticLink';
import useI18n from '@i18n';
import {
    ICommonTable,
    ICommonTableColumn,
    IOrderOption,
} from '@pages/statistics/common/interfaces';

import { MONTH_DATE_REGEXP, MONTH_YEAR_REGEXP } from '../config';

// TODO: таблица не должна знать про это, нужно вынести выше
const columnsWithLeftAlign: string[] = [
    'offerName',
    'creativeName',
    'os',
    'material',
    'countryCode',
];

const Table: React.FC<ICommonTable> = ({
    columns,
    data,
    total,
    onSort,
    orderBy,
    getLinkWithParams,
    onNextPage,
    isLoadedAll,
    isFetching = false,
    totalError = null,
    totalTextNode = null,
}) => {
    const { t } = useI18n('Statistic');
    const { t: tCommon } = useI18n('Common');
    const ref = useRef(null);

    if (!data.length && !totalError && !isFetching) {
        return null;
    }

    if (totalError) {
        const summaryRequestId: string = totalError?.response?.headers?.get('request-id');
        const summaryErrorMessage: string = summaryRequestId
            ? `request-id: ${summaryRequestId}`
            : String(totalError);

        return (
            <p className="mt-12 mb-12 text-center">
                {t('Failed to load summary statistics')}
                <br />
                <b>{summaryErrorMessage}</b>
            </p>
        );
    }

    const totalIsNotEmpty = !!Object.keys(total).length;

    const isLoadMoreToShow: boolean = !isFetching && !isLoadedAll && data.length !== 0;

    const columnsWithLink: string[] = ['date', 'month'];

    const getDateValue = (value: string, key: string): string => {
        if (key === 'date') {
            const dateParts = value.match(MONTH_DATE_REGEXP)?.slice(1);
            if (!dateParts) return value;

            const [day, month, year] = dateParts;

            return `${day} ${t(`${month}`)} ${year}`;
        }

        if (key === 'month') {
            const monthParts = value.match(MONTH_YEAR_REGEXP)?.slice(1);

            if (!monthParts) return value;

            const [month, year] = monthParts;

            return `${t(`${month}`)} ${year}`;
        }

        return value;
    };

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const renderTdByParams = (item, key, getLinkWithParams) => {
        const classes = clsx({
            'p-[16px] text-center whitespace-nowrap text-[12px] sm:text-[16px]': true,
            'text-left': columnsWithLeftAlign.includes(key),
        });
        const value = columnsWithLink.includes(key) ? getDateValue(item[key], key) : item[key];

        return (
            <td key={key} className={classes}>
                {columnsWithLink.includes(key) ? (
                    <BaseSemanticLink
                        to={getLinkWithParams(item[key], key)}
                        className="underline"
                        target="_blank"
                    >
                        {value}
                    </BaseSemanticLink>
                ) : (
                    value
                )}
            </td>
        );
    };

    // Todo: move table head cells rendering to standalone function

    return (
        <>
            <div className="relative">
                <div className="overflow-x-scroll max-w-[100%] sm:overflow-auto">
                    {!isFetching && (
                        <table className="border-collapse w-[100%] min-w-[970px]" ref={ref}>
                            <thead>
                                <tr>
                                    {columns.map(
                                        ({ key, order, circleColor, hint }: ICommonTableColumn) => {
                                            // Проверяем, есть ли группировка по данному полю
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            // нужно избавиться от вероятного boolean в конфиге
                                            const isOrderedBy = orderBy.order === order;

                                            // Для смены направления сортировки
                                            const orderConfig = {
                                                asc: 'desc' as const,
                                                desc: 'asc' as const,
                                            };

                                            // Выставляем направление по умолчанию. Для полей без сортировки будет 'asc'
                                            const orderDirection =
                                                (isOrderedBy && orderBy.orderDirection) || 'asc';

                                            const iconClass = clsx({
                                                'text-blueGray-600': isOrderedBy,
                                                'text-blueGray-300': !isOrderedBy,
                                            });

                                            const iconConfig = {
                                                asc: faCaretUp,
                                                desc: faCaretDown,
                                            };

                                            const params: IOrderOption = {
                                                order,
                                                orderDirection: orderConfig[orderDirection],
                                            };

                                            const TooltipComponent: React.ElementType = hint
                                                ? TooltipPro
                                                : Fragment;

                                            return (
                                                <th
                                                    key={order ? order.toString() : key}
                                                    className="p-3 font-normal text-[14px] text-blueGray-300 whitespace-nowrap hover:cursor-pointer"
                                                    onClick={() => Boolean(order) && onSort(params)}
                                                >
                                                    <TooltipComponent
                                                        title={
                                                            <p className="whitespace-normal">
                                                                {hint}
                                                            </p>
                                                        }
                                                        placement="right"
                                                    >
                                                        <div>
                                                            {circleColor && (
                                                                <span
                                                                    className="inline-block rounded-full w-[8px] h-[8px] mr-[4px]"
                                                                    style={{
                                                                        background:
                                                                            circleColor ||
                                                                            '#001540',
                                                                    }}
                                                                />
                                                            )}
                                                            <span className="mr-1">{key}</span>
                                                            {order && (
                                                                <FontAwesomeIcon
                                                                    className={iconClass}
                                                                    icon={
                                                                        iconConfig[orderDirection]
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                    </TooltipComponent>
                                                </th>
                                            );
                                        },
                                    )}
                                </tr>
                            </thead>
                            <tbody className="text-brandBlue-500">
                                {totalIsNotEmpty && (
                                    <tr className="text-white bg-blueGray-500">
                                        <td className="p-3 text-center whitespace-nowrap text-[12px] sm:text-[16px]">
                                            {t('Total')}
                                        </td>
                                        {Object.keys(total).map(key => (
                                            <td
                                                className="p-3 text-center whitespace-nowrap text-[12px] sm:text-[16px]"
                                                key={key}
                                            >
                                                {total[key]}
                                            </td>
                                        ))}
                                    </tr>
                                )}
                                {totalTextNode && (
                                    <tr className="text-white bg-brandBlue-300">
                                        <td
                                            className="p-3 text-left whitespace-nowrap text-[12px] sm:text-[16px]"
                                            colSpan={columns.length}
                                        >
                                            {totalTextNode}
                                        </td>
                                    </tr>
                                )}
                                {data.map((item: IGetCommonStatDataFormatted) => (
                                    <tr
                                        key={item.id}
                                        className="hover:cursor-pointer hover:bg-green-200/50 odd:bg-[#f1f5f9]"
                                    >
                                        {Object.keys(item).map(key =>
                                            renderTdByParams(item, key, getLinkWithParams),
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <div className="mx-auto mt-3 mb-[80px] hack-max-w-fit-content">
                {isFetching && <Loader />}

                {!isFetching && isLoadMoreToShow && (
                    <Button size="middle" onClick={onNextPage}>
                        {tCommon('Load more')}
                    </Button>
                )}
            </div>
        </>
    );
};

export default Table;
